import React, { forwardRef, memo, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import Uppy from '@uppy/core';
import { Dashboard } from '@uppy/react';
import XHR from '@uppy/xhr-upload';
import ImageEditor from '@uppy/image-editor';
import '@uppy/core/dist/style.css';
// import '@uppy/dashboard/dist/style.css';
import '@uppy/image-editor/dist/style.css';
import Modal from '@lib/components/Modal/Modal';
import SupportiveMessage, {
  SupportiveMessageProps,
} from '@lib/components/SupportiveMessage/SupportiveMessage';
import { getMaxFileSize, imageSizeLimit } from '@lib/enums/mimeTypes';
import './FileUploaderUppyXHR.scss';
import './dashboard.css';
import { AttachmentValue, OnChangeMultiple, OnChangeSingle } from './types';
import { UploaderView } from './enums';
import getLocale from './utils/getLocale';
import getAttachmentVariables from './utils/getAttachmentVariables';
import styles from './FileUploaderUppyXHR.module.scss';

export * from './enums';
export * from './types';

const { VITE_API_URL: API_DOMAIN, VITE_AUTH_SCOPE } = import.meta.env;

interface Props {
  allowedFileTypes: string[];
  className?: string;
  maxFileSize?: number;
  maxNumberOfFiles?: number;
  note?: string;
  onChange: OnChangeSingle | OnChangeMultiple;
  openButton: ({
    onClick,
  }: {
    onClick: () => void;
  }) => React.ReactElement | null;
  openUploaderModalOnInit?: boolean;
  useUploadStrategy?: boolean;
  type: UploaderView;
  value?: AttachmentValue | AttachmentValue[];
  supportiveMessageProps?: SupportiveMessageProps;
}

const FileUploaderUppyXHR = forwardRef<HTMLInputElement, Props>(
  (props, ref) => {
    const {
      allowedFileTypes,
      className = '',
      maxFileSize = imageSizeLimit,
      maxNumberOfFiles = 1,
      note,
      onChange,
      openButton,
      openUploaderModalOnInit = false,
      supportiveMessageProps,
      type,
      useUploadStrategy = true,
      value,
    } = props;
    const { t, i18n } = useTranslation();
    const currentLocale = i18n.language;
    const [modalOpen, setModalOpen] = useState(
      _isEmpty(value) ? openUploaderModalOnInit : false,
    );
    const [uppyInstance, setUppyInstance] = useState<Uppy | null>(null);

    const handleOpen = () => setModalOpen(true);
    const handleClose = () => setModalOpen(false);

    useEffect(() => {
      const restrictions = {
        maxFileSize,
        allowedFileTypes,
        maxNumberOfFiles,
      };
      const uppy = useUploadStrategy
        ? new Uppy({
            id: 'uppy',
            restrictions,
            autoProceed: false,
            // debug: MODE === 'development',
          })
            .use(XHR, {
              endpoint: `${API_DOMAIN}/files/upload`,
              withCredentials: true,
              headers: { 'X-Auth-Scope': VITE_AUTH_SCOPE },
            })
            .use(ImageEditor, {})
        : new Uppy({
            id: 'uppy',
            restrictions,
            autoProceed: false,
          }).use(ImageEditor, {});

      uppy.on('file-added', (file) => {
        const fileType = file.extension ? `.${file.extension}` : '';
        const maxAllowedSize = getMaxFileSize(fileType);
        if (file.size > maxAllowedSize) {
          uppy.removeFile(file.id);
          uppy.info(
            t('file-add-validation-error', {
              name: file.name,
              size: maxAllowedSize / 1024 / 1024,
              type: fileType,
            }),
            'error',
            8000,
          );
        }
      });

      uppy.on('complete', (result) => {
        const files = _get(result, 'successful');
        const isSuccessful = _get(result, 'successful.length', 0) > 0;

        if (!useUploadStrategy) {
          const localFiles = files.map((file) => ({
            filename: file.name,
            size: file.size,
            mimeType: file.type,
            file: file.data as File,
          })) as AttachmentValue[];

          if (maxNumberOfFiles === 1) {
            (onChange as OnChangeSingle)(localFiles[0]);
          } else {
            (onChange as OnChangeMultiple)(localFiles);
          }

          if (type === UploaderView.modal) handleClose();
          return;
        }

        if (maxNumberOfFiles === 1) {
          const file = _get(result, 'successful.0');
          if (file?.id) {
            const newValue = getAttachmentVariables(file);
            (onChange as OnChangeSingle)(newValue);
          }
        } else if (maxNumberOfFiles > 1) {
          const newValue: AttachmentValue[] = [];
          files.forEach((file) => {
            if (file?.id) {
              newValue.push(getAttachmentVariables(file));
            }
          });
          if (value && Array.isArray(value)) {
            (onChange as OnChangeMultiple)([...value, ...newValue]);
          } else {
            (onChange as OnChangeMultiple)(newValue);
          }
        }
        if (type === UploaderView.modal && isSuccessful) {
          handleClose();
        }
      });

      uppy.on('upload-error', (file, error, response) => {
        // TODO: Handle error, maybe by showing an alert to the user
        // eslint-disable-next-line no-console
        console.error('Failed to upload', file?.name, error, response);
      });

      setUppyInstance(uppy);

      return () => {
        uppy.close();
      };
    }, [
      allowedFileTypes,
      maxFileSize,
      maxNumberOfFiles,
      onChange,
      t,
      type,
      useUploadStrategy,
      value,
    ]);

    if (uppyInstance === null) return null;

    const uppyDashboard = (
      <div
        className={classNames(styles.uppyRoot, className, {
          [styles.uppyRootWithMessage]: supportiveMessageProps,
        })}
      >
        <Dashboard
          uppy={uppyInstance}
          note={note}
          proudlyDisplayPoweredByUppy={false}
          plugins={['ImageEditor']}
          showSelectedFiles
          showRemoveButtonAfterComplete
          locale={getLocale(currentLocale)}
          // @ts-ignore TODO: fix this
          mobileNativeCamera
        />
        {supportiveMessageProps && (
          <div style={{ marginTop: '16px' }}>
            <SupportiveMessage
              color={supportiveMessageProps.color}
              text={supportiveMessageProps.text}
              actions={supportiveMessageProps.actions}
              fullWidth
            />
          </div>
        )}
      </div>
    );

    return (
      <div>
        <input type="hidden" ref={ref} />
        {type === UploaderView.modal && (
          <>
            {openButton({ onClick: handleOpen })}
            <Modal
              isOpen={modalOpen}
              onClose={handleClose}
              title={
                maxNumberOfFiles > 1 ? t('upload-files') : t('upload-file')
              }
            >
              {uppyDashboard}
            </Modal>
          </>
        )}
        {type === UploaderView.dragDrop && uppyDashboard}
      </div>
    );
  },
);

export default memo(FileUploaderUppyXHR);
